/**
 * Privacy Policy Page
 *
 * Public legal page displaying NinjaCash privacy policy.
 * Accessible without authentication for compliance (GDPR, CCPA, Play Store).
 * Matches Tokyo Night aesthetic with glass morphism.
 */

import { Link } from 'react-router-dom';
import './LegalPage.css';

export default function PrivacyPage() {
  return (
    <div className="legal-page">
      <div className="legal-container">
        {/* Header */}
        <header className="legal-header">
          <Link to="/" className="legal-back">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Back to App
          </Link>
          <div className="legal-brand">
            <span className="legal-logo">$</span>
            <span className="legal-app-name">NinjaCash</span>
          </div>
        </header>

        {/* Content */}
        <article className="legal-content">
          <h1 className="legal-title">Privacy Policy</h1>
          <p className="legal-meta">Last Updated: January 19, 2026</p>

          {/* Overview */}
          <section className="legal-section">
            <h2>Overview</h2>
            <p>
              NinjaCash is a personal finance tracking application. This Privacy Policy explains how we collect, use, store, and protect your information when you use our application and services.
            </p>
            <div className="legal-highlight">
              <h3>Key Points</h3>
              <ul>
                <li>Your financial data is yours - we never sell it</li>
                <li>App works offline - your data stays on your device by default</li>
                <li>Sync is optional and encrypted</li>
                <li>You can delete all your data at any time</li>
              </ul>
            </div>
          </section>

          {/* Section 1 */}
          <section className="legal-section">
            <h2>1. Information We Collect</h2>
            
            <h3>1.1 Information You Provide</h3>
            <div className="legal-table-wrapper">
              <table className="legal-table">
                <thead>
                  <tr>
                    <th>Data Type</th>
                    <th>Examples</th>
                    <th>Purpose</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Account Information</td>
                    <td>Email address, display name</td>
                    <td>Account creation, login, sync</td>
                  </tr>
                  <tr>
                    <td>Financial Data</td>
                    <td>Transactions, accounts, categories, budgets</td>
                    <td>Core app functionality</td>
                  </tr>
                  <tr>
                    <td>Preferences</td>
                    <td>Theme, currency, settings</td>
                    <td>Personalization</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3>1.2 Information Collected Automatically</h3>
            <div className="legal-table-wrapper">
              <table className="legal-table">
                <thead>
                  <tr>
                    <th>Data Type</th>
                    <th>Examples</th>
                    <th>Purpose</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Device Information</td>
                    <td>Device type, OS version</td>
                    <td>App compatibility, debugging</td>
                  </tr>
                  <tr>
                    <td>Usage Data</td>
                    <td>Feature usage, crash reports</td>
                    <td>Improving the app</td>
                  </tr>
                  <tr>
                    <td>Sync Metadata</td>
                    <td>Last sync time, device ID</td>
                    <td>Multi-device synchronization</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3>1.3 Information We Do NOT Collect</h3>
            <ul>
              <li>Bank account credentials or passwords</li>
              <li>Social Security numbers or government IDs</li>
              <li>Location data (GPS)</li>
              <li>Contact lists or phone data</li>
              <li>Photos, camera, or microphone access</li>
            </ul>
          </section>

          {/* Section 2 */}
          <section className="legal-section">
            <h2>2. How We Use Your Information</h2>
            <div className="legal-table-wrapper">
              <table className="legal-table">
                <thead>
                  <tr>
                    <th>Purpose</th>
                    <th>Legal Basis (GDPR)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Provide app functionality</td>
                    <td>Contract performance</td>
                  </tr>
                  <tr>
                    <td>Sync data across devices</td>
                    <td>Contract performance</td>
                  </tr>
                  <tr>
                    <td>Send account-related emails</td>
                    <td>Legitimate interest</td>
                  </tr>
                  <tr>
                    <td>Improve the app</td>
                    <td>Legitimate interest</td>
                  </tr>
                  <tr>
                    <td>Respond to support requests</td>
                    <td>Legitimate interest</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p><strong>We do NOT use your data for:</strong></p>
            <ul>
              <li>Advertising or marketing to third parties</li>
              <li>Selling to data brokers</li>
              <li>Profiling for credit decisions</li>
              <li>Any purpose unrelated to the app</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section className="legal-section">
            <h2>3. Data Storage and Security</h2>
            
            <h3>3.1 Local Storage (Free Tier)</h3>
            <p>Your data is stored locally on your device using IndexedDB:</p>
            <ul>
              <li>Protected by your device's built-in encryption</li>
              <li>Never leaves your device unless you enable sync</li>
              <li>You have full control</li>
            </ul>

            <h3>3.2 Cloud Storage (Sync Enabled)</h3>
            <p>If you enable sync, your data is stored on our servers with multiple layers of protection:</p>
            <div className="legal-table-wrapper">
              <table className="legal-table">
                <thead>
                  <tr>
                    <th>Layer</th>
                    <th>Protection</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>In Transit</td>
                    <td>TLS 1.3 encryption (HTTPS)</td>
                  </tr>
                  <tr>
                    <td>At Rest</td>
                    <td>AES-256 encryption (database)</td>
                  </tr>
                  <tr>
                    <td>Access Control</td>
                    <td>Only you can access your data</td>
                  </tr>
                  <tr>
                    <td>Location</td>
                    <td>Servers in United States</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 4 */}
          <section className="legal-section">
            <h2>4. Data Sharing</h2>
            
            <h3>4.1 We Do NOT Share Your Data With:</h3>
            <ul>
              <li>Advertisers</li>
              <li>Data brokers</li>
              <li>Marketing companies</li>
              <li>Analytics companies (beyond basic crash reporting)</li>
              <li>Any third party for commercial purposes</li>
            </ul>

            <h3>4.2 Limited Sharing</h3>
            <p>We may share data only in these circumstances:</p>
            <div className="legal-table-wrapper">
              <table className="legal-table">
                <thead>
                  <tr>
                    <th>Situation</th>
                    <th>What's Shared</th>
                    <th>With Whom</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Authentication</td>
                    <td>Email only</td>
                    <td>Google (OAuth)</td>
                  </tr>
                  <tr>
                    <td>Legal requirement</td>
                    <td>As required by law</td>
                    <td>Government authorities</td>
                  </tr>
                  <tr>
                    <td>With your consent</td>
                    <td>As you specify</td>
                    <td>Third party you choose</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 5 */}
          <section className="legal-section">
            <h2>5. Your Rights</h2>
            
            <h3>5.1 All Users</h3>
            <div className="legal-table-wrapper">
              <table className="legal-table">
                <thead>
                  <tr>
                    <th>Right</th>
                    <th>How to Exercise</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Access</td>
                    <td>View all your data in the app</td>
                  </tr>
                  <tr>
                    <td>Correction</td>
                    <td>Edit any data in the app</td>
                  </tr>
                  <tr>
                    <td>Deletion</td>
                    <td>Delete transactions, accounts, or entire account</td>
                  </tr>
                  <tr>
                    <td>Export</td>
                    <td>Export all data as JSON</td>
                  </tr>
                  <tr>
                    <td>Withdraw Consent</td>
                    <td>Disable sync, delete account</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3>5.2 GDPR Rights (EU/EEA Users)</h3>
            <ul>
              <li><strong>Right to Access</strong> - Request a copy of all your data</li>
              <li><strong>Right to Rectification</strong> - Correct inaccurate data</li>
              <li><strong>Right to Erasure</strong> - "Right to be forgotten" - delete all data</li>
              <li><strong>Right to Restrict Processing</strong> - Limit how we use your data</li>
              <li><strong>Right to Data Portability</strong> - Receive data in machine-readable format</li>
              <li><strong>Right to Object</strong> - Object to processing based on legitimate interest</li>
            </ul>

            <h3>5.3 CCPA/CPRA Rights (California Users)</h3>
            <ul>
              <li><strong>Right to Know</strong> - Know what data is collected and why</li>
              <li><strong>Right to Delete</strong> - Request deletion of personal information</li>
              <li><strong>Right to Opt-Out</strong> - Opt out of sale of personal info (we don't sell)</li>
              <li><strong>Right to Non-Discrimination</strong> - Equal service regardless of privacy choices</li>
            </ul>
            <p><strong>We do not sell your personal information.</strong></p>
          </section>

          {/* Section 6 */}
          <section className="legal-section">
            <h2>6. Data Retention</h2>
            <div className="legal-table-wrapper">
              <table className="legal-table">
                <thead>
                  <tr>
                    <th>Data Type</th>
                    <th>Retention Period</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Local data</td>
                    <td>Until you delete it</td>
                  </tr>
                  <tr>
                    <td>Synced data</td>
                    <td>Until you delete it or close account</td>
                  </tr>
                  <tr>
                    <td>Account info</td>
                    <td>30 days after account deletion</td>
                  </tr>
                  <tr>
                    <td>Crash reports</td>
                    <td>90 days</td>
                  </tr>
                  <tr>
                    <td>Server logs</td>
                    <td>30 days</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p>After account deletion: All financial data is permanently deleted immediately. Account metadata is deleted within 30 days.</p>
          </section>

          {/* Section 7 */}
          <section className="legal-section">
            <h2>7. Account Deletion</h2>
            <p>You can delete your account and all associated data:</p>
            <ol>
              <li><strong>In-App:</strong> Settings &gt; Account &gt; Delete Account</li>
              <li><strong>Via GitHub:</strong> <a href="https://github.com/TakashiKyoto/ninjacash/issues" target="_blank" rel="noopener noreferrer">Open a deletion request issue</a></li>
            </ol>
            <p><strong>What happens:</strong></p>
            <ul>
              <li>All your transactions, accounts, categories, and budgets are permanently deleted</li>
              <li>Your email and account info are removed within 30 days</li>
              <li>This action cannot be undone</li>
            </ul>
          </section>

          {/* Section 8 */}
          <section className="legal-section">
            <h2>8. Children's Privacy</h2>
            <p>
              NinjaCash is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us.
            </p>
          </section>

          {/* Section 9 */}
          <section className="legal-section">
            <h2>9. Cookies and Tracking</h2>
            <h3>What We Use</h3>
            <ul>
              <li><strong>Session cookie</strong> - Keep you logged in (session duration)</li>
              <li><strong>Preference cookie</strong> - Remember your settings (1 year)</li>
            </ul>
            <h3>What We Do NOT Use</h3>
            <ul>
              <li>Third-party tracking cookies</li>
              <li>Advertising cookies</li>
              <li>Analytics cookies (beyond basic crash reporting)</li>
              <li>Fingerprinting</li>
            </ul>
          </section>

          {/* Section 10 - Third-Party Services */}
          <section className="legal-section">
            <h2>10. Third-Party Services</h2>
            <p>NinjaCash uses the following third-party services:</p>
            
            <h3>10.1 Authentication</h3>
            <div className="legal-table-wrapper">
              <table className="legal-table">
                <thead>
                  <tr>
                    <th>Service</th>
                    <th>Purpose</th>
                    <th>Privacy Policy</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Google OAuth</td>
                    <td>Sign in with Google</td>
                    <td><a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3>10.2 Infrastructure</h3>
            <div className="legal-table-wrapper">
              <table className="legal-table">
                <thead>
                  <tr>
                    <th>Service</th>
                    <th>Purpose</th>
                    <th>Privacy Policy</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Cloudflare</td>
                    <td>Frontend hosting, analytics</td>
                    <td><a href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noopener noreferrer">Cloudflare Privacy Policy</a></td>
                  </tr>
                  <tr>
                    <td>Railway</td>
                    <td>API hosting, database</td>
                    <td><a href="https://railway.app/legal/privacy" target="_blank" rel="noopener noreferrer">Railway Privacy Policy</a></td>
                  </tr>
                  <tr>
                    <td>Sentry</td>
                    <td>Error tracking, crash reports</td>
                    <td><a href="https://sentry.io/privacy/" target="_blank" rel="noopener noreferrer">Sentry Privacy Policy</a></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3>10.3 Payments</h3>
            <div className="legal-table-wrapper">
              <table className="legal-table">
                <thead>
                  <tr>
                    <th>Service</th>
                    <th>Purpose</th>
                    <th>Privacy Policy</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Polar.sh</td>
                    <td>Payment processing</td>
                    <td><a href="https://polar.sh/legal/privacy" target="_blank" rel="noopener noreferrer">Polar Privacy Policy</a></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 11 */}
          <section className="legal-section">
            <h2>11. Changes to This Policy</h2>
            <p>We may update this Privacy Policy from time to time. We will notify you of any material changes by:</p>
            <ul>
              <li>Posting the new policy on our website</li>
              <li>Updating the "Last Updated" date</li>
              <li>Sending an email notification (for significant changes)</li>
            </ul>
            <p>Your continued use of the app after changes constitutes acceptance of the updated policy.</p>
          </section>

          {/* Section 12 */}
          <section className="legal-section">
            <h2>12. Contact Us</h2>
            <p>For privacy-related questions or to exercise your rights, please use GitHub Issues:</p>
            <ul>
              <li><strong>GitHub:</strong> <a href="https://github.com/TakashiKyoto/ninjacash/issues" target="_blank" rel="noopener noreferrer">github.com/TakashiKyoto/ninjacash/issues</a></li>
            </ul>
            <p>You can also find this link on the login screen and in Settings. <strong>Response Time:</strong> We aim to respond to all privacy requests within 30 days.</p>
          </section>

          {/* Summary */}
          <section className="legal-section">
            <h2>Summary</h2>
            <div className="legal-summary">
              <div className="summary-item">
                <span className="summary-question">Do we sell your data?</span>
                <span className="summary-answer no">No, never</span>
              </div>
              <div className="summary-item">
                <span className="summary-question">Do we share with advertisers?</span>
                <span className="summary-answer no">No</span>
              </div>
              <div className="summary-item">
                <span className="summary-question">Can you delete your data?</span>
                <span className="summary-answer yes">Yes, anytime</span>
              </div>
              <div className="summary-item">
                <span className="summary-question">Is your data encrypted?</span>
                <span className="summary-answer yes">Yes (in transit + at rest)</span>
              </div>
              <div className="summary-item">
                <span className="summary-question">Does the app work offline?</span>
                <span className="summary-answer yes">Yes (local-first)</span>
              </div>
            </div>
          </section>
        </article>

        {/* Footer */}
        <footer className="legal-footer">
          <p>This Privacy Policy was last updated on January 19, 2026.</p>
          <div className="legal-footer-links">
            <Link to="/terms">Terms of Service</Link>
            <span className="legal-divider">|</span>
            <Link to="/">Back to NinjaCash</Link>
          </div>
        </footer>
      </div>
    </div>
  );
}
